import { Link } from 'react-router-dom';

import { formatDate, formatMoney } from '../../lib/format';
import type { EventOpportunity } from '../../lib/catalog';

// The investment card, used by the EventOpportunity block and therefore by both the CMS
// composition and the emergency one. Never modified during the CMS migration: it states
// investment terms and eligibility language, which a renderer change has no business
// touching.

/**
 * Two states, driven by what the engine actually returned.
 *
 * The financial figures are withheld server-side from anyone who is not a fully
 * approved investor, so this component does not "hide" them — for most visitors
 * those fields never arrive at all. Branching on `financialsVisible` keeps the
 * UI honest about which response it is rendering.
 */
export function OpportunityCard({ opportunity }: { opportunity: EventOpportunity }) {
  const closes = opportunity.closesAt ? formatDate(opportunity.closesAt) : null;

  if (!opportunity.financialsVisible) {
    return (
      <aside className="opportunity opportunity--teaser">
        <p className="opportunity__eyebrow">Investment opportunity</p>
        <h3 className="opportunity__title">{opportunity.title}</h3>
        {opportunity.description && (
          <p className="opportunity__description">{opportunity.description}</p>
        )}

        <p className="opportunity__locked">
          <span className="opportunity__lock" aria-hidden="true">
            🔒
          </span>
          Investment terms and funding progress are available to approved investors.
        </p>

        {closes && (
          <p className="opportunity__closes">
            Open until <span className="mono">{closes}</span>
          </p>
        )}

        <Link to="/register" className="button button--secondary button--block">
          Become an investor
        </Link>
        <p className="opportunity__disclaimer">
          Investing carries risk, including loss of capital. Investor accounts are reviewed
          and approved before any figures or terms are shared.
        </p>
      </aside>
    );
  }

  const percent = Math.min(Math.max(opportunity.percentFunded ?? 0, 0), 100);

  return (
    <aside className="opportunity glass-card">
      <p className="opportunity__eyebrow">Investment opportunity</p>
      <h3 className="opportunity__title">{opportunity.title}</h3>
      {opportunity.description && (
        <p className="opportunity__description">{opportunity.description}</p>
      )}

      <div className="opportunity__progress" role="img" aria-label={`${percent}% funded`}>
        <div className="opportunity__progress-bar" style={{ inlineSize: `${percent}%` }} />
      </div>

      <dl className="opportunity__stats">
        <div>
          <dt>Raised</dt>
          <dd className="mono">{formatMoney(opportunity.raisedCents ?? 0)}</dd>
        </div>
        <div>
          <dt>Target</dt>
          <dd className="mono">{formatMoney(opportunity.targetCents ?? 0)}</dd>
        </div>
        <div>
          <dt>Funded</dt>
          <dd className="mono">{percent}%</dd>
        </div>
        <div>
          <dt>Minimum</dt>
          <dd className="mono">{formatMoney(opportunity.minimumInvestmentCents ?? 0)}</dd>
        </div>
      </dl>

      {closes && (
        <p className="opportunity__closes">
          Closes <span className="mono">{closes}</span>
        </p>
      )}

      <Link to="/investor/opportunities" className="button button--secondary button--block">
        View full terms
      </Link>
      <p className="opportunity__disclaimer">
        Investing carries risk, including loss of capital.
      </p>
    </aside>
  );
}
